import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';

import { signalBiqAgenticCancel } from '../utils/api';
import { createLiveChatsStore } from './biqLiveChatsStore';

/**
 * React bindings for the live chat store (see biqLiveChatsStore.js). The
 * provider sits above the BiQ page (DashboardLayout), so turns also survive
 * navigating away from /biq.
 */

const BiqLiveChatsContext = createContext(null);

export const BiqLiveChatsProvider = ({ children }) => {
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createLiveChatsStore({
      onCancel: (token) => {
        void signalBiqAgenticCancel(token);
      },
    });
  }
  const store = storeRef.current;

  // Abort leftover streams when the dashboard unmounts (logout / leaving the app shell).
  useEffect(() => () => store.clear(), [store]);

  return <BiqLiveChatsContext.Provider value={store}>{children}</BiqLiveChatsContext.Provider>;
};

export const useBiqLiveChats = () => {
  const store = useContext(BiqLiveChatsContext);
  if (!store) {
    throw new Error('useBiqLiveChats must be used within a BiqLiveChatsProvider');
  }
  return store;
};

/** Subscribe to one chat's live entry (stable reference until that entry changes). */
export const useLiveChat = (id) => {
  const store = useBiqLiveChats();
  const getSnapshot = useCallback(() => store.get(id), [store, id]);
  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
};

/** Ids of chats currently generating (stable array until the set changes). */
export const useBusyChatIds = () => {
  const store = useBiqLiveChats();
  return useSyncExternalStore(store.subscribe, store.getBusyIds, store.getBusyIds);
};

/** Ids of chats that finished a turn while the user was looking at another chat. */
export const useUnreadChatIds = () => {
  const store = useBiqLiveChats();
  return useSyncExternalStore(store.subscribe, store.getUnreadIds, store.getUnreadIds);
};
